import { Button } from '@strategy4kids/ui';
import { useEffect, useState } from 'react';
import type { Edition } from '../edition/types.js';
import { communityPack } from '../lib/community-packs.js';
import { packsForEdition } from '../lib/card-packs.js';
import { ApiError, type MarketplacePackInfo, marketplace } from '../services/api.js';
import { useCardPackStore } from '../state/card-pack-store.js';
import { useCommunityPackStore } from '../state/community-pack-store.js';

type Listed = Omit<MarketplacePackInfo, 'files'>;

/**
 * More packs, straight from the marketplace: what it lists for this edition (the kids edition is only ever given official, everyone-rated
 * packs by the service), minus the ones already built in or added. Picking one adds it to this device and chooses it. The images stay on the
 * marketplace; the built-in packs remain the offline default.
 */
export function MarketplacePackBrowser({ edition, marketplaceUrl }: { readonly edition: Edition; readonly marketplaceUrl: string }) {
  const [state, setState] = useState<{ readonly phase: 'loading' } | { readonly phase: 'ready'; readonly packs: readonly Listed[] } | { readonly phase: 'error'; readonly text: string }>({ phase: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const added = useCommunityPackStore((s) => s.packs);
  const add = useCommunityPackStore((s) => s.add);
  const setPack = useCardPackStore((s) => s.setPack);

  useEffect(() => {
    let live = true;
    marketplace
      .listPacks(edition)
      .then((r) => live && setState({ phase: 'ready', packs: r.packs }))
      .catch((e: unknown) => live && setState({ phase: 'error', text: e instanceof ApiError && e.status === 0 ? 'Cannot reach the pack marketplace right now.' : 'The pack list could not be loaded.' }));
    return () => {
      live = false;
    };
  }, [edition]);

  if (state.phase === 'loading') return <p className="mt-3 text-base text-fg-muted" data-testid="pack-browser-loading">Looking for more packs…</p>;
  if (state.phase === 'error') return <p className="mt-3 text-base font-semibold text-red-700 dark:text-red-300" role="status" data-testid="pack-browser-error">{state.text}</p>;

  const have = new Set([...packsForEdition(edition).map((p) => p.id), ...added.map((p) => p.id)]);
  const fresh = state.packs.filter((p) => !have.has(p.id));

  async function pick(id: string) {
    setBusy(id);
    setMessage('');
    try {
      const pack = communityPack(await marketplace.getPack(id, edition), marketplaceUrl, edition);
      add(pack);
      setPack(pack.id);
      setMessage(`Added ${pack.name}. It is your card pack now.`);
    } catch {
      setMessage('That pack could not be added. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-3 flex flex-col gap-2" data-testid="pack-browser">
      <h3 className="text-base font-bold text-ink">More packs</h3>
      {fresh.length === 0 ? <p className="text-base text-fg-muted" data-testid="pack-browser-empty">No more packs yet. Check back soon.</p> : null}
      <ul className="flex flex-col gap-2">
        {fresh.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`pack-browser-row-${p.id}`}>
            <span className="text-base text-ink">
              <span className="font-bold">{p.name}</span>
              {p.tagline ? <span className="text-fg-muted"> {p.tagline}</span> : null}
            </span>
            <Button variant="secondary" disabled={busy !== null} data-testid={`pack-browser-add-${p.id}`} onClick={() => void pick(p.id)}>
              {busy === p.id ? 'Adding…' : 'Add'}
            </Button>
          </li>
        ))}
      </ul>
      <p className="text-base font-semibold text-fg" role="status" aria-live="polite" data-testid="pack-browser-status">{message}</p>
    </div>
  );
}
